import React from "react";
import { Phone, MessageSquare, ExternalLink, AlertTriangle, ShieldAlert, Heart, X } from "lucide-react";

interface CrisisSupportCardProps {
  severity: "none" | "low_concern" | "crisis_support" | "urgent_crisis";
  onDismiss: () => void;
  onOpenSafetyResources?: () => void;
  className?: string;
}

export function CrisisSupportCard({ severity, onDismiss, onOpenSafetyResources, className = "" }: CrisisSupportCardProps) {
  if (severity === "none") return null;

  // Render slightly different colors, icons, and texts based on severity to ensure a supportive, calm experience
  const isUrgent = severity === "urgent_crisis";
  const isLow = severity === "low_concern";

  const cardBg = isUrgent 
    ? "bg-rose-50/95 border-rose-250 ring-1 ring-rose-200" 
    : isLow
    ? "bg-slate-50/95 border-slate-200"
    : "bg-indigo-50/95 border-indigo-200 ring-1 ring-indigo-100";

  const titleColor = isUrgent 
    ? "text-rose-950 font-black" 
    : isLow
    ? "text-slate-800 font-bold"
    : "text-indigo-950 font-extrabold";

  const iconContainerBg = isUrgent 
    ? "bg-rose-100 text-rose-600" 
    : isLow
    ? "bg-slate-100 text-slate-500"
    : "bg-indigo-100 text-indigo-600";

  return (
    <div className={`p-5 rounded-xl border transition-all duration-300 relative shadow-xs ${cardBg} ${className}`} id="crisis-support-overlay">
      {/* Dismiss button */}
      <button 
        onClick={onDismiss}
        className="absolute top-3.5 right-3.5 p-1.5 rounded-full hover:bg-black/5 text-slate-400 hover:text-slate-600 transition cursor-pointer"
        aria-label="Dismiss support message" // i18n-ignore
        title="Continue Search" // i18n-ignore
        id="dismiss-crisis-card"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-4 pr-6">
        <div className={`p-2 rounded-xl shrink-0 ${iconContainerBg}`}>
          {isUrgent ? (
            <ShieldAlert className="w-5 h-5" />
          ) : isLow ? (
            <Heart className="w-5 h-5 text-slate-500" />
          ) : (
            <AlertTriangle className="w-5 h-5" />
          )}
        </div>

        <div className="space-y-3 flex-1">
          <div>
            <h3 className={`text-sm tracking-tight ${titleColor}`}>
              {isUrgent 
                ? "Support is available right now" 
                : isLow
                ? "Taking care of your mental well-being"
                : "Support is available"}
            </h3>
            
            <p className="mt-1.5 text-xs text-slate-700 leading-relaxed font-medium">
              {isUrgent ? (
                <span>
                  If you or someone else might be in danger or thinking about self-harm, you can call or text{" "}
                  <strong className="font-extrabold text-rose-950 underline decoration-rose-400">988</strong> to reach the Suicide &amp; Crisis Lifeline.{" "}
                  <strong className="font-extrabold text-rose-950">If there is immediate danger, call emergency services now.</strong> Free, confidential support is available 24/7.
                </span>
              ) : isLow ? (
                <span>
                  If you are feeling overwhelmed, lonely, or going through a challenging phase, please know you do not have to walk it alone. 
                  Standard confidential resources are always open and ready to listen.
                </span>
              ) : (
                <span>
                  If you or someone else might be going through a tough time, you can call or text{" "}
                  <strong className="font-extrabold text-indigo-950 underline decoration-indigo-400">988</strong> to reach the Suicide &amp; Crisis Lifeline. 
                  Free, confidential support is available 24/7.
                </span>
              )}
            </p>

            {!isLow && (
              <p className="mt-2 text-[11px] text-slate-500 font-semibold">
                Outside the U.S.? Please reach out to your local emergency number or crisis service.
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            {/* Primary Action Buttons */}
            <a
              href="tel:988"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-white rounded-lg text-xs font-bold shadow-sm transition-all cursor-pointer ${
                isUrgent ? "bg-rose-600 hover:bg-rose-700" : "bg-indigo-600 hover:bg-indigo-700"
              }`}
            >
              <Phone className="w-3.5 h-3.5" />
              Call 988
            </a>
            
            <a
              href="sms:988"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 rounded-lg text-xs font-bold shadow-xs transition-all cursor-pointer"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              Text 988
            </a>

            <a
              href="https://988lifeline.org/chat/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 rounded-lg text-xs font-medium shadow-xs transition-all cursor-pointer"
            >
              <ExternalLink className="w-3.5 h-3.5 text-slate-500" />
              Chat with 988
            </a>

            {onOpenSafetyResources && (
              <button
                type="button"
                onClick={onOpenSafetyResources}
                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-medium transition cursor-pointer"
              >
                More Resources
              </button>
            )}

            <button
              type="button"
              onClick={onDismiss}
              className="px-3 py-1.5 hover:bg-slate-100 text-slate-500 rounded-lg text-xs font-medium transition cursor-pointer"
            >
              Continue search
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
